import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import TaskStatusCard from '../components/Maintenance/TaskStatusCard';
import SystemHealthPanel from '../components/Maintenance/SystemHealthPanel';
import TaskTimeline from '../components/Maintenance/TaskTimeline';
import AlertSettings from '../components/Maintenance/AlertSettings';
import RailwayMonitoringPanel from '../components/Maintenance/RailwayMonitoringPanel';
import { maintenanceApi } from '../services/maintenanceApi';

function Maintenance() {
  const [tasks, setTasks] = useState([]);
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data on mount and set up auto-refresh
  useEffect(() => {
    fetchData();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setError(null);

      const [tasksResult, healthResult, logsResult] = await Promise.allSettled([
        maintenanceApi.getTasks(),
        maintenanceApi.getHealth(),
        maintenanceApi.getLogs()
      ]);

      if (tasksResult.status === 'fulfilled') setTasks(tasksResult.value?.data?.tasks || []);
      if (healthResult.status === 'fulfilled') setHealth(healthResult.value?.data || null);
      if (logsResult.status === 'fulfilled') setLogs(logsResult.value?.data?.logs || []);

      // Only show error if all three failed
      const allFailed = [tasksResult, healthResult, logsResult].every(r => r.status === 'rejected');
      if (allFailed) {
        setError('Maintenance task monitoring is unavailable. The maintenance API endpoints are not configured.');
      }
    } catch (err) {
      console.error('Error fetching maintenance data:', err);
      setError('Failed to load maintenance data. Please check if the backend server is running.');
    } finally {
      setLoading(false);
    }
  };

  const handleRunTask = async (taskId) => {
    try {
      await maintenanceApi.runTask(taskId);
      // Refresh data after running task
      await fetchData();
    } catch (err) {
      console.error('Error running task:', err);
      alert('Failed to run task. Please try again.');
    }
  };

  const handleToggleTask = async (taskId, enabled) => {
    try {
      await maintenanceApi.updateTask(taskId, { enabled });
      // Refresh data after updating task
      await fetchData();
    } catch (err) {
      console.error('Error updating task:', err);
      alert('Failed to update task. Please try again.');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1.5, sm: 2, md: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Railway Server Monitoring - Primary section */}
      <Box sx={{ mb: 4 }}>
        <RailwayMonitoringPanel />
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* System Health Overview */}
      {health && (
        <Box sx={{ mb: 4 }}>
          <SystemHealthPanel health={health} />
        </Box>
      )}

      {/* Maintenance Tasks */}
      {tasks.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Maintenance Tasks
          </Typography>
          <Grid container spacing={3}>
            {tasks.map((task) => (
              <Grid item xs={12} sm={6} md={4} key={task.id}>
                <TaskStatusCard
                  task={task}
                  onRunNow={() => handleRunTask(task.id)}
                  onToggle={(enabled) => handleToggleTask(task.id, enabled)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Task Execution Timeline */}
      {logs.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Recent Activity
          </Typography>
          <TaskTimeline logs={logs} />
        </Box>
      )}

      {/* Alert Settings */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Alert Configuration
        </Typography>
        <AlertSettings />
      </Box>
    </Container>
  );
}

export default Maintenance;

